import { useState, useMemo, useEffect } from 'react';

import { AccordionDropdown } from '@backrs_ui/common-ui';

import { FitPathsLegend } from '@backrs_ui/grow-ui/src/pages/CareerPath/molecules/careerPathPage/FitPathsLegend';
import {
  getCareerPathsForCategory,
  getCategoryIcon,
  getCareerPathFitColor,
  sortCareerCategories,
  getStrongFitCountForCategory,
} from '@backrs_ui/grow-ui/src/pages/CareerPath/util';
import { useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';
import { selectUserSurveyResults } from '@backrs_ui/grow-ui/src/redux/userResponse/userResponse';

import { Box, Typography, Button, SxProps, Theme, keyframes } from '@mui/material';

type CareerPathSelectorProps = Readonly<{
  onSubmit: (formattedSelection: string) => void;
  onSkip?: () => void;
  disabled?: boolean;
}>;

export const CareerPathSelector = ({
  onSubmit,
  onSkip,
  disabled = false,
}: CareerPathSelectorProps) => {
  const [selectedCategoryNames, setSelectedCategoryNames] = useState<Set<string>>(new Set());
  const [selectedPathNames, setSelectedPathNames] = useState<Set<string>>(new Set());
  const [visibleCount, setVisibleCount] = useState(0);

  const careerCategories = useAppSelector((state) => state.careerPath.careerCategories);
  const careerPaths = useAppSelector((state) => state.careerPath.careerPaths);
  const surveyResults = useAppSelector(selectUserSurveyResults);
  const careerPathSuggestions = surveyResults?.careerPathSuggestions;

  const sortedCategories = useMemo(
    () => sortCareerCategories(careerCategories, careerPathSuggestions),
    [careerCategories, careerPathSuggestions],
  );

  useEffect(() => {
    if (sortedCategories.length === 0) return;

    setVisibleCount(0);
    const interval = setInterval(() => {
      setVisibleCount((prev) => {
        if (prev >= sortedCategories.length - 1) clearInterval(interval);

        return prev + 1;
      });
    }, 10);

    return () => clearInterval(interval);
  }, [sortedCategories.length]);

  const handleCategorySelectionChange = (categoryName: string, selected: boolean) => {
    if (disabled) return;

    setSelectedCategoryNames((prev) => {
      const next = new Set(prev);

      if (selected) {
        next.add(categoryName);
      } else {
        next.delete(categoryName);
      }

      return next;
    });
  };

  const handlePathSelectionChange = (pathName: string, selected: boolean) => {
    if (disabled) return;

    setSelectedPathNames((prev) => {
      const next = new Set(prev);

      if (selected) {
        next.add(pathName);
      } else {
        next.delete(pathName);
      }

      return next;
    });
  };

  const handleSubmit = () => {
    const parts: string[] = [];

    if (selectedCategoryNames.size > 0) {
      const categoryList = Array.from(selectedCategoryNames)
        .map((name) => `- ${name}`)
        .join('\n');

      parts.push(`**Fields:**\n${categoryList}`);
    }

    if (selectedPathNames.size > 0) {
      const pathList = Array.from(selectedPathNames)
        .map((name) => `- ${name}`)
        .join('\n');

      parts.push(`**Paths:**\n${pathList}`);
    }

    const formattedSelection = parts.join('\n\n');

    onSubmit(formattedSelection);
  };

  const hasSelection = selectedCategoryNames.size > 0 || selectedPathNames.size > 0;

  return (
    <Box sx={containerSx}>
      <FitPathsLegend />

      <Box sx={accordionContainerSx}>
        {sortedCategories.slice(0, visibleCount).map((category) => {
          const categoryPaths = getCareerPathsForCategory(category.id, careerPaths);
          const strongFitCount = careerPathSuggestions
            ? getStrongFitCountForCategory(
                category.id.toString(),
                careerPathSuggestions,
                careerCategories,
              )
            : 0;

          const subtitleItems = categoryPaths.map((path) => ({
            text: path.name,
            showSubtitleIcon: true,
            subtitleIconColor: getCareerPathFitColor(
              careerPathSuggestions?.find((cp) => cp.careerPath.id === path.id)?.scoreSection,
            ),
          }));

          return (
            <Box key={category.id} sx={staggeredItemSx}>
              <AccordionDropdown
                id={`career-path-category-${category.slug}`}
                title={category.short_name}
                iconName={getCategoryIcon(category.slug)}
                iconDiameter="16px"
                subtitles={subtitleItems}
                badgeCount={strongFitCount}
                selectable
                selectedItems={selectedPathNames}
                onSelectionChange={handlePathSelectionChange}
                headerSelectable
                isHeaderSelected={selectedCategoryNames.has(category.short_name)}
                onHeaderSelectionChange={(selected) =>
                  handleCategorySelectionChange(category.short_name, selected)
                }
                sxOverrides={{
                  header: headerSx,
                  iconContainer: iconContainerSx,
                  title: titleSx,
                }}
              />
            </Box>
          );
        })}
      </Box>

      <Box sx={submitButtonContainerSx}>
        {onSkip && (
          <Button
            id="career-path-selector-skip-btn"
            hierarchy="Tertiary color"
            size="sm"
            onClick={onSkip}
            disabled={disabled}
          >
            <Typography variant="text-sm-semibold">Skip</Typography>
          </Button>
        )}
        <Button
          id="career-path-selector-submit-btn"
          hierarchy="Primary"
          size="md"
          onClick={handleSubmit}
          disabled={disabled || !hasSelection}
        >
          <Typography variant="text-sm-semibold">Submit</Typography>
        </Button>
      </Box>
    </Box>
  );
};

const containerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-lg'],
  padding: backrs.spacing['spacing-xl'],
  backgroundColor: backrs.colors['Colors/Background/bg-primary'],
  borderRadius: backrs.radius['radius-xl'],
  border: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
  width: '100%',
});

const accordionContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
  gap: backrs.spacing['spacing-md'],
});

const headerSx: SxProps<Theme> = ({ backrs }) => ({
  height: '44px',
  padding: backrs.spacing['spacing-sm'],
  '&.Mui-expanded': {
    height: '44px',
  },
});

const iconContainerSx: SxProps<Theme> = ({ backrs }) => ({
  width: '28px',
  height: '28px',
  padding: backrs.spacing['spacing-xs'],
  borderRadius: backrs.radius['radius-md'],
});

const titleSx: SxProps<Theme> = {
  typography: 'text-md-semibold',
};

const submitButtonContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  gap: backrs.spacing['spacing-md'],
  alignItems: 'center',
  mt: backrs.spacing['spacing-md'],
  width: '100%',
});

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
`;

const staggeredItemSx: SxProps<Theme> = {
  animation: `${fadeIn} 0.3s ease-out forwards`,
};
